import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const highLevelModules = [
  'Bytes', 'Text', 'Format', 'Containers', 'Algorithm', 'Sequence',
  'OptionResult', 'Numeric', 'Error', 'Diagnostics', 'Log', 'Inspect',
  'Path', 'IO', 'Stream', 'Time', 'Process', 'Terminal',
  'Synchronization', 'Task', 'EventLoop', 'DNS', 'HTTP', 'URL',
  'JSON', 'Binary', 'DataFormats', 'Hash', 'Glob', 'CLI',
  'Object', 'Function', 'HirSsa', 'Code', 'Test', 'Benchmark', 'Debug',
];

const nativeModules = [
  'Memory', 'Native', 'Ffi', 'FileSystem', 'Environment', 'Thread',
  'Net', 'TLS', 'Compression', 'Crypto', 'Regex', 'Compiler',
];

const slugify = name => name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
const moduleId = (layer, name) => `std-module-${layer}-${slugify(name)}`;
const sectionHref = id => `#/stdlib?section=${encodeURIComponent(id)}`;

function ModuleDirectory({ title, layer, modules, activeSection }) {
  return (
    <div className="stdlib-directory-group">
      <div className="section">{title}</div>
      {modules.map(name => {
        const id = moduleId(layer, name);
        return (
          <a className={activeSection === id ? 'active' : ''} key={id} href={sectionHref(id)}>
            {name}
          </a>
        );
      })}
    </div>
  );
}

function ModuleGrid({ layer, modules, activeSection }) {
  return (
    <div className="module-grid">
      {modules.map(name => {
        const id = moduleId(layer, name);
        return (
          <article className={`module-card${activeSection === id ? ' active' : ''}`} id={id} key={id}>
            <span className="module-layer">{layer === 'high-level' ? 'high-level' : 'native'}</span>
            <h4>{name}</h4>
            <a className="module-anchor" href={sectionHref(id)} aria-label={`link to ${name} module`}>
              #
            </a>
          </article>
        );
      })}
    </div>
  );
}

export default function StdLib() {
  const location = useLocation();
  const activeSection = new URLSearchParams(location.search).get('section') || '';

  useEffect(() => {
    if (!activeSection) return;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(activeSection)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeSection]);

  return (
    <div className="doc-layout stdlib-layout">
      <nav className="doc-sidebar stdlib-directory" aria-label="standard library modules">
        <div className="stdlib-directory-heading">standard library</div>
        <a href={sectionHref('std-overview')} className={activeSection === 'std-overview' ? 'active' : ''}>overview</a>
        <ModuleDirectory
          title="high-level · 37"
          layer="high-level"
          modules={highLevelModules}
          activeSection={activeSection}
        />
        <ModuleDirectory
          title="native · 12"
          layer="native"
          modules={nativeModules}
          activeSection={activeSection}
        />
        <a href={sectionHref('std-coverage')} className={activeSection === 'std-coverage' ? 'active' : ''}>coverage</a>
      </nav>

      <main className="doc-content stdlib-content">
        <header className="doc-hero" id="std-overview">
          <span className="eyebrow">49 first-party modules</span>
          <h2>standard library</h2>
          <p>
            37 high-level modules with 500 promises and 12 native modules with 205 promises.
            All 705 operations are verified on Windows x64 and Linux x64.
          </p>
          <div className="stdlib-stats" aria-label="standard library coverage">
            <span><strong>37</strong> high-level</span>
            <span><strong>12</strong> native</span>
            <span><strong>705</strong> operations</span>
          </div>
        </header>

        <section className="stdlib-section" aria-labelledby="std-high-level-heading">
          <div className="section-heading">
            <div>
              <span className="eyebrow">portable abstractions</span>
              <h3 id="std-high-level-heading">high-level modules</h3>
            </div>
            <span className="section-count">37 modules</span>
          </div>
          <ModuleGrid layer="high-level" modules={highLevelModules} activeSection={activeSection} />
        </section>

        <section className="stdlib-section" aria-labelledby="std-native-heading">
          <div className="section-heading">
            <div>
              <span className="eyebrow">platform foundations</span>
              <h3 id="std-native-heading">native modules</h3>
            </div>
            <span className="section-count">12 modules</span>
          </div>
          <ModuleGrid layer="native" modules={nativeModules} activeSection={activeSection} />
        </section>

        <section className="coverage-panel" id="std-coverage">
          <span className="eyebrow">verified coverage</span>
          <h3>every promise is tracked</h3>
          <p>
            <code>stdlib/native-manifest.json</code> and{' '}
            <code>stdlib/high-level-manifest.json</code> are validated by dedicated tools and backed by
            C++ behavior tests on both platforms. No stubs and no fixed-success mocks.
          </p>
          <a href="https://github.com/Alex000291/celestc" target="_blank" rel="noreferrer">
            explore the full design on GitHub →
          </a>
        </section>
      </main>
    </div>
  );
}
